// App Section - Projects Dashboard
// Displays user AI projects in grid view
// Follows the section-based architecture pattern

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppManager } from './managers/app-manager';
import { MainLayout } from '../../components/Layout';
import { logger, useManager } from '../../core';
import { settingsUtils, envUtils, eventUtils } from '../../core/utils';
import { useAuthRequired } from '../../contexts/AuthContext';
import { navigationManager } from '../../managers/navigation-manager';
import {
  AppGrid,
  AppSearchBar,
  AppFiltersModal,
  AppContextMenu
} from './components';
import { RevoltButton, RevoltModal, RevoltInput, RevoltTextarea, AppCreationErrorModal, RemixModal, TemplateBadge, RevoltSegmentedControl } from '../../components';
import DropdownSelect from '../../components/DropdownSelect';
import { getSelectableAppTypes, getDefaultAppType, normalizeAppType } from '../../core';

const APP_FILTER_SEARCH_DEBOUNCE_MS = 300;

const APP_SORT_VALUES = new Set(['name', 'lastModified', 'created']);
const APP_FAVORITES_VALUES = new Set(['favorites', 'nonFavorites']);
const APP_OWNED_VALUES = new Set(['owned', 'nonOwned']);

function normalizeAppTagToken(str) {
  return String(str || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');
}

export function AppSection() {
  const { manager: appManager, isInitialized } = useManager(AppManager);
  const [searchParams, setSearchParams] = useSearchParams();

  // Require authentication to access app section
  // Payload can be used in the future for things like:
  // - Pre-selecting a project to open
  // - Opening a specific workspace
  // Search/sort/favorites/owned/tags are restored from the URL (shareable /app?... links).
  const { user, returnPayload, clearReturnPayload } = useAuthRequired({
    payload: {
      action: 'viewProjects',
      section: 'app',
      timestamp: Date.now()
    }
    // Note: Apps section is now accessible to all authenticated users
    // Only app creation requires beta tester access
  });

  const filterParamsKey = searchParams.toString();
  const urlQuery = searchParams.get('query') || '';
  const rawSort = searchParams.get('sort') || '';
  const urlSort = APP_SORT_VALUES.has(rawSort) ? rawSort : 'lastModified';
  const rawFav = searchParams.get('favorites') || '';
  const urlFavorites = APP_FAVORITES_VALUES.has(rawFav) ? rawFav : 'all';
  const rawOwned = searchParams.get('owned') || '';
  const urlOwned = APP_OWNED_VALUES.has(rawOwned) ? rawOwned : 'all';
  const urlPinFavoritesTop = searchParams.get('pinFavorites') === '1';
  const urlTags = useMemo(() => {
    const raw = searchParams.get('tags') || '';
    const parts = raw
      .split(',')
      .map((t) => normalizeAppTagToken(t))
      .filter(Boolean);
    return [...new Set(parts)];
  }, [filterParamsKey]);

  const [localSearchTerm, setLocalSearchTerm] = useState(urlQuery);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveModalProject, setLeaveModalProject] = useState(null);
  const [showRemixModal, setShowRemixModal] = useState(false);
  const [remixModalProject, setRemixModalProject] = useState(null);
  const [remixModalLoading, setRemixModalLoading] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalProject, setDeleteModalProject] = useState(null);
  const [deleteInput, setDeleteInput] = useState('');
  const [isEasyDelete, setIsEasyDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [easyAppManagementEnabled, setEasyAppManagementEnabled] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const [newAppTitle, setNewAppTitle] = useState('');
  const [newAppDescription, setNewAppDescription] = useState('');
  const [newAppTemplate, setNewAppTemplate] = useState('p5');
  const [newAppType, setNewAppType] = useState(() => getDefaultAppType().id);
  const [newAppWorkingDirectory, setNewAppWorkingDirectory] = useState('');
  const [workingDirectoryError, setWorkingDirectoryError] = useState('');
  const [templateOptions, setTemplateOptions] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templateError, setTemplateError] = useState('');
  const [templatesLoadedOnce, setTemplatesLoadedOnce] = useState(false);
  const [projectsVersion, setProjectsVersion] = useState(0);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdApp, setCreatedApp] = useState(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    const uiMode = envUtils.device.getRecommendedUIMode();
    return uiMode === 'mobile';
  });
  const [contextMenuState, setContextMenuState] = useState({
    isOpen: false,
    x: 0,
    y: 0,
    project: null
  });

  // Load evaluated settings for developer features
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await settingsUtils.getEvaluatedSettings();
        setEasyAppManagementEnabled(settings.easyAppManagement);
      } catch (error) {
        console.error('Failed to load settings for app management:', error);
        setEasyAppManagementEnabled(false);
      }
    };

    loadSettings();
  }, []);

  useEffect(() => {
    setLocalSearchTerm(urlQuery);
  }, [urlQuery]);

  const updateUrlFromQuery = useCallback(
    (query) => {
      const newParams = new URLSearchParams(searchParams);
      if (query && query.trim()) {
        newParams.set('query', query.trim());
      } else {
        newParams.delete('query');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const debouncedUrlUpdateRef = useRef(null);
  useEffect(() => {
    debouncedUrlUpdateRef.current = eventUtils.debounce(
      updateUrlFromQuery,
      APP_FILTER_SEARCH_DEBOUNCE_MS
    );
  }, [updateUrlFromQuery]);

  const debouncedUrlUpdate = useCallback((...args) => {
    debouncedUrlUpdateRef.current?.(...args);
  }, []);

  const handleSearchChange = useCallback(
    (value) => {
      setLocalSearchTerm(value);
      debouncedUrlUpdate(value);
    },
    [debouncedUrlUpdate]
  );

  const handleSortChange = useCallback(
    (newSortOption) => {
      const newParams = new URLSearchParams(searchParams);
      const v = newSortOption || 'lastModified';
      if (v && v !== 'lastModified') {
        newParams.set('sort', v);
      } else {
        newParams.delete('sort');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleFavoritesChange = useCallback(
    (value) => {
      const newParams = new URLSearchParams(searchParams);
      if (value && value !== 'all') {
        newParams.set('favorites', value);
      } else {
        newParams.delete('favorites');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleOwnedChange = useCallback(
    (value) => {
      const newParams = new URLSearchParams(searchParams);
      if (value && value !== 'all') {
        newParams.set('owned', value);
      } else {
        newParams.delete('owned');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleTagsChange = useCallback(
    (newTags) => {
      const newParams = new URLSearchParams(searchParams);
      if (newTags?.length > 0) {
        newParams.set('tags', newTags.join(','));
      } else {
        newParams.delete('tags');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handlePinFavoritesTopChange = useCallback(
    (enabled) => {
      const newParams = new URLSearchParams(searchParams);
      if (enabled) {
        newParams.set('pinFavorites', '1');
      } else {
        newParams.delete('pinFavorites');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleClearQueryFromUrl = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('query');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const handleResize = () => {
      const uiMode = envUtils.device.getRecommendedUIMode();
      const mobile = uiMode === 'mobile';
      setIsMobile(mobile);
      appManager.setIsMobile(mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [appManager]);

  // Handle return payload after authentication redirect
  useEffect(() => {
    if (returnPayload) {
      // Future: Use payload data to restore state or perform actions
      // Examples:
      // - Restore search/filter state: setSearchTerm(returnPayload.searchTerm)
      // - Open specific project: handleOpenWorkspace(returnPayload.projectId)
      // - Apply filters: setShowFavoritesOnly(returnPayload.favoritesOnly)

      logger.log({
        tags: "section|app|payload",
        color1: "cyan"
      }, "Return payload received:", returnPayload);

      // Clear payload after processing
      clearReturnPayload();
    }
  }, [returnPayload, clearReturnPayload]);


  // Load template options when create modal opens
  useEffect(() => {
    const loadTemplates = async () => {
      if (!showCreateModal) return;
      if (isLoadingTemplates) return;
      if (templatesLoadedOnce && templateOptions.length > 0) return;

      setIsLoadingTemplates(true);
      setTemplateError('');

      try {
        const res = await fetch('/api/app-templates', { credentials: 'include' });
        const data = await res.json();

        if (!res.ok || !Array.isArray(data)) {
          throw new Error(data?.error || 'Failed to load templates');
        }

        let options = data.map((t, idx) => ({
          value: t.uniqueId || t.id || `template-${idx}`,
          label: t.displayName || t.uniqueId || 'Template',
          appType: t.appType,
          description: t.description || '',
        }));

        // Sort options to put 'basic' first if it exists
        options.sort((a, b) => {
          if (a.value === 'basic') return -1;
          if (b.value === 'basic') return 1;
          return 0; // Keep original order for others
        });

        const defaultOption =
          options.find((o) => o.value === 'p5') ||
          options[0];

        setTemplateOptions(options);
        setTemplatesLoadedOnce(true);

        const hasExisting = newAppTemplate && options.some((o) => o.value === newAppTemplate);
        if (!hasExisting && defaultOption) {
          setNewAppTemplate(defaultOption.value);
        }
      } catch (err) {
        logger.error({
          tags: "section|app|template-error",
          color1: "red"
        }, "Template load error:", err);
        setTemplateError(err.message || 'Template load failed');
        const fallback = [
          { value: 'p5', label: 'P5', description: 'A JS browser project using p5.js', appType: 'browser' },
          { value: 'website', label: 'Website', description: 'Hosted web app template', appType: 'website' },
          { value: 'desktop', label: 'Desktop App', description: 'Remote desktop app template', appType: 'desktop' },
        ];
        setTemplateOptions(fallback);
        setTemplatesLoadedOnce(true);
        const fallbackDefault =
          fallback.find((o) => o.value === 'p5') ||
          fallback[0];
        const hasExistingFallback = newAppTemplate && fallback.some((o) => o.value === newAppTemplate);
        if (!hasExistingFallback && fallbackDefault) {
          setNewAppTemplate(fallbackDefault.value);
        }
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    loadTemplates();
  }, [showCreateModal, templateOptions.length, isLoadingTemplates, newAppTemplate, templatesLoadedOnce]);

  // Load projects for the user (memberships) once initialized
  useEffect(() => {
    const loadProjects = async () => {
      if (!isInitialized || !user) return;
      setIsLoadingProjects(true);
      await appManager.loadUserProjects(user);
      setProjectsVersion((v) => v + 1);
      setIsLoadingProjects(false);
    };
    loadProjects();
  }, [isInitialized, user, appManager]);

  const resetCreateForm = () => {
    setNewAppTitle('');
    setNewAppDescription('');
    const defaultTemplate =
      templateOptions.find((o) => o.value === 'basic') ||
      templateOptions[0];
    setNewAppTemplate(defaultTemplate ? defaultTemplate.value : 'basic');
  };

  useEffect(() => {
    if (showCreateModal) {
      resetCreateForm();
      setNewAppType(getDefaultAppType().id);
      setNewAppWorkingDirectory('');
      setWorkingDirectoryError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal]);

  // Clear working-directory state if user switches away from local.
  useEffect(() => {
    if (newAppType !== 'local') {
      if (newAppWorkingDirectory) setNewAppWorkingDirectory('');
      if (workingDirectoryError) setWorkingDirectoryError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newAppType]);

  const handleChooseWorkingDirectory = async () => {
    try {
      const api = typeof window !== 'undefined' ? window.dialogAPI : null;
      if (!api || typeof api.showOpenDialog !== 'function') {
        setWorkingDirectoryError('Folder picker is only available inside Revolt Desktop.');
        return;
      }
      const result = await api.showOpenDialog({
        title: 'Choose a working directory for this app',
        properties: ['openDirectory', 'createDirectory'],
      });
      if (!result || result.canceled) return;
      const picked = Array.isArray(result.filePaths) ? result.filePaths[0] : null;
      if (picked) {
        setNewAppWorkingDirectory(picked);
        setWorkingDirectoryError('');
      }
    } catch (err) {
      logger.error(
        { tags: 'section|app|working-directory', color1: 'red' },
        'Failed to choose working directory:',
        err
      );
      setWorkingDirectoryError(err?.message || 'Failed to choose working directory');
    }
  };

  // Selectable app types depend on environment; on web we only see types whose
  // createEnvironment is "any", on desktop we see desktop-only types too.
  const appTypeOptions = useMemo(() => {
    const isDesktop = envUtils.device.isDesktopApp();
    return getSelectableAppTypes({ isDesktop }).map((t) => ({
      value: t.id,
      label: t.shortName + (t.experimental ? ' (beta)' : ''),
    }));
  }, []);

  // Template options filtered by the currently selected app type
  const filteredTemplateOptions = useMemo(() => {
    const type = normalizeAppType(newAppType);
    return templateOptions.filter((o) => normalizeAppType(o.appType) === type);
  }, [templateOptions, newAppType]);

  // Keep the selected template valid for the chosen app type.
  // For local apps, template is optional: default to empty (no template) unless
  // the user explicitly picks one from the filtered list.
  useEffect(() => {
    if (!showCreateModal) return;
    const isLocal = newAppType === 'local';
    if (!filteredTemplateOptions.length) {
      if (newAppTemplate) setNewAppTemplate('');
      return;
    }
    const hasCurrent = filteredTemplateOptions.some((o) => o.value === newAppTemplate);
    if (!hasCurrent) {
      if (isLocal) {
        setNewAppTemplate('');
        return;
      }
      const defaultForType =
        filteredTemplateOptions.find((o) => o.value === 'basic') ||
        filteredTemplateOptions.find((o) => o.value === 'p5') ||
        filteredTemplateOptions[0];
      setNewAppTemplate(defaultForType ? defaultForType.value : '');
    }
  }, [showCreateModal, filteredTemplateOptions, newAppTemplate, newAppType]);

  // Sync URL-derived filters onto the manager immediately before listing.
  // (A separate useEffect runs too late: useMemo would read stale manager fields.)
  const displayProjects = useMemo(() => {
    if (!isInitialized) return [];
    appManager.searchTerm = urlQuery;
    appManager.sortBy = urlSort;
    appManager.favoritesFilter = urlFavorites;
    appManager.ownedFilter = urlOwned;
    appManager.tagsFilter = urlTags;
    appManager.pinFavoritesTop = urlPinFavoritesTop;
    return appManager.getDisplayProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appManager,
    isInitialized,
    urlQuery,
    urlSort,
    urlFavorites,
    urlOwned,
    urlTags,
    urlPinFavoritesTop,
    projectsVersion,
  ]);

  // Don't render until manager is initialized
  if (!isInitialized) {
    return (
      <MainLayout>
        <div className="app-section">
          <div className="loading-container">
            <div>Loading your projects...</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Note: useAuthRequired automatically redirects to auth if not authenticated
  // This check is just a safety measure - the hook handles the redirect
  if (!user) {
    return (
      <MainLayout>
        <div className="app-section">
          <div className="loading-container">
            <div>Authenticating...</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  logger.log({
    tags: "section|app|render",
    color1: "lime"
  }, "AppSection rendering");

  const handleToggleFavorite = (projectId) => {
    // Pass callback to trigger UI updates for optimistic changes
    appManager.toggleFavorite(projectId, () => {
      setProjectsVersion((v) => v + 1);
    });
  };

  const handleOpenWorkspace = async (projectId) => {
    await appManager.openWorkspace(projectId);
  };

  const handleOpenPlayspace = async (projectId) => {
    if (appManager?.openPlayspace) {
      await appManager.openPlayspace(projectId);
    } else {
      // Fallback to workspace until playspace route exists everywhere
      await appManager.openWorkspace(projectId);
    }
  };

  const handleOpenSettings = async (projectId) => {
    await appManager.openSettings(projectId);
  };

  const handleCloseContextMenu = () => {
    setContextMenuState({
      isOpen: false,
      x: 0,
      y: 0,
      project: null
    });
  };

  const handleProjectContextMenu = (project, event) => {
    event.preventDefault();
    setContextMenuState({
      isOpen: true,
      x: event.clientX,
      y: event.clientY,
      project
    });
  };

  const handleOpenCreateModal = () => {
    resetCreateForm();
    setCreateLoading(false);
    setShowCreateModal(true);
  };

  const handleLeaveApp = (project) => {
    setLeaveModalProject(project);
    setShowLeaveModal(true);
  };

  const handleRemixApp = (project) => {
    setRemixModalProject(project);
    setShowRemixModal(true);
    logger.log({ tags: 'section|app|remix', color1: 'purple' }, 'Remix modal opened for:', project.name);
  };

  const handleDeleteApp = async () => {
    if (!deleteModalProject) return;

    setDeleteLoading(true);
    try {
      const success = await appManager.deleteApp(deleteModalProject.id);
      if (success) {
        // Refresh the projects list
        await appManager.loadUserProjects(user);
        setProjectsVersion((v) => v + 1);
        logger.log({ tags: 'section|app|delete', color1: 'red' }, 'App deleted successfully:', deleteModalProject.name);
      }
    } catch (error) {
      console.error('Failed to delete app:', error);
      alert('Failed to delete app. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleOpenDeleteModal = (project) => {
    setDeleteModalProject(project);
    setDeleteInput('');
    setIsEasyDelete(true); // This is always true for easy app management
    setDeleteLoading(false);
    setShowDeleteModal(true);
  };

  const handleConfirmRemix = async (project) => {
    logger.log({ tags: 'section|app|remix', color1: 'purple' }, 'Remixing app:', project.name);

    // Set loading state on the modal
    setRemixModalLoading(true);

    try {
      // Call the remix API endpoint
      const response = await fetch('/api/apps/remix', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          sourceAppId: project._id || project.id,
          name: `${project.name} (Remix)`, // Add "(Remix)" suffix
          description: project.description || '',
        }),
      });

      const result = await response.json();

      if (result.success) {
        logger.log({ tags: 'section|app|remix', color1: 'lime' }, 'App remixed successfully:', result.app.name);

        // Close the modal
        setShowRemixModal(false);
        setRemixModalProject(null);
        setRemixModalLoading(false);

        // Navigate to the workspace (same as workspace button)
        await handleOpenWorkspace(result.app._id);
      } else {
        logger.error({ tags: 'section|app|remix', color1: 'red' }, 'Failed to remix app:', result.error);

        // Show error to user (could add a toast notification here)
        alert(`Failed to remix app: ${result.error}`);

        setRemixModalLoading(false);
      }
    } catch (error) {
      logger.error({ tags: 'section|app|remix', color1: 'red' }, 'Error remixing app:', error);

      // Show error to user
      alert(`Failed to remix app: ${error.message}`);

      setRemixModalLoading(false);
    }
  };

  const handleConfirmLeaveApp = async () => {
    if (!leaveModalProject) return;

    try {
      const response = await fetch(`/api/app-memberships`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          appId: leaveModalProject.id,
          targetUserId: user.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to leave app');
      }

      // Remove the app from the manager's projects array
      if (appManager.projects) {
        appManager.projects = appManager.projects.filter(p => p.id !== leaveModalProject.id);
      }

      // Close modal
      setShowLeaveModal(false);
      setLeaveModalProject(null);

      // Trigger UI update by incrementing projects version
      setProjectsVersion((v) => v + 1);

      logger.log({ tags: 'section|app|leave', color1: 'lime' }, 'Successfully left app', {
        appId: leaveModalProject.id,
        appName: leaveModalProject.name
      });
    } catch (error) {
      logger.error({ tags: 'section|app|leave', color1: 'red' }, 'Leave app failed', error);
      // For now, just close the modal on error. Could add error modal later
      setShowLeaveModal(false);
      setLeaveModalProject(null);
    }
  };

  const handleSubmitCreate = async () => {
    setCreateLoading(true);

    const payload = {
      name: newAppTitle,
      description: newAppDescription,
      template: newAppTemplate,
      appType: newAppType,
      workingDirectoryPath: newAppType === 'local' ? newAppWorkingDirectory : undefined,
      checkpoint: null,
      ownerId: user?.id || null,
    };

    try {
      const res = await fetch('/api/apps', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data?.success) {
        // Parse structured error response
        const errorMessage = data?.error || 'Failed to create app';
        const errorCode = data?.errorCode || 'VALIDATION_ERROR';
        const details = data?.details || {};

        // Log structured error for debugging
        logger.error(
          { tags: 'section|app|create', color1: 'red' },
          `App creation failed: ${errorCode}`,
          { errorCode, details, errorMessage }
        );

        // Set error data and show modal
        setErrorData({
          errorCode,
          errorMessage,
          details
        });
        setShowErrorModal(true);
        throw new Error(errorMessage);
      }

      const app = data.app;

      // Refresh projects list for the user
      await appManager.loadUserProjects(user);
      setProjectsVersion((v) => v + 1);

      logger.log({ tags: 'section|app|create', color1: 'lime' }, 'App created', {
        appId: app?._id,
        template: app?.template,
        appType: app?.appType,
      });

      // Close create modal and show success modal
      setShowCreateModal(false);
      setCreatedApp(app);
      setShowSuccessModal(true);
    } catch (error) {
      logger.error({ tags: 'section|app|create', color1: 'red' }, 'Create app failed', error);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setCreatedApp(null);
    resetCreateForm();
  };

  const handleGoToSettings = () => {
    if (createdApp) {
      navigationManager.navigateToAppSettings(createdApp._id);
    }
    handleSuccessModalClose();
  };

  const handleGoToWorkspace = () => {
    if (createdApp) {
      navigationManager.navigateToPath(`/workspace/${createdApp._id}`);
    }
    handleSuccessModalClose();
  };

  const selectedTemplate = templateOptions.find((o) => o.value === newAppTemplate);

  return (
    <MainLayout contentClassName="main-content--app">
      <div className="app-section">
        {/* Section Header */}
        <div className="app-section-header">
          <div className="app-section-header-top">
            <h1 className="app-section-title">Your Apps</h1>
            <div className="app-section-actions">
              <RevoltButton variant="accent" onClick={handleOpenCreateModal} title="Create New App">
                {isMobile ? (
                  <i className="fa-solid fa-plus"></i>
                ) : (
                  <>
                    <i className="fa-solid fa-plus"></i> Create New App
                  </>
                )}
              </RevoltButton>
            </div>
          </div>
          <p className="app-section-subtitle">Manage and build your AI-powered applications</p>
        </div>

        {/* Toolbar */}
        <div className="app-management-toolbar">
          <AppSearchBar
            searchTerm={localSearchTerm}
            onSearchChange={handleSearchChange}
            sortBy={urlSort}
            onSortChange={handleSortChange}
            favoritesFilter={urlFavorites}
            onFavoritesChange={handleFavoritesChange}
            ownedFilter={urlOwned}
            onOwnedChange={handleOwnedChange}
            tags={urlTags}
            onTagsChange={handleTagsChange}
            pinFavoritesTop={urlPinFavoritesTop}
            onPinFavoritesTopChange={handlePinFavoritesTopChange}
            isMobile={isMobile}
            onOpenFilters={isMobile ? () => setShowFiltersModal(true) : undefined}
          />
        </div>

        {isMobile && (
          <AppFiltersModal
            isOpen={showFiltersModal}
            onClose={() => setShowFiltersModal(false)}
            sortBy={urlSort}
            onSortChange={handleSortChange}
            favoritesFilter={urlFavorites}
            onFavoritesChange={handleFavoritesChange}
            ownedFilter={urlOwned}
            onOwnedChange={handleOwnedChange}
            tags={urlTags}
            onTagsChange={handleTagsChange}
            onClearQuery={handleClearQueryFromUrl}
            pinFavoritesTop={urlPinFavoritesTop}
            onPinFavoritesTopChange={handlePinFavoritesTopChange}
          />
        )}

        {/* Projects Display */}
        <div className="app-section-content">
          {isLoadingProjects ? (
            <div className="app-section-loading">
              <div className="app-section-spinner"></div>
              <p className="app-section-loading-text">Loading your projects...</p>
            </div>
          ) : (
            <>
              <AppGrid
                projects={displayProjects}
                manager={appManager}
                onToggleFavorite={handleToggleFavorite}
                onOpenWorkspace={handleOpenWorkspace}
                onOpenPlayspace={handleOpenPlayspace}
                onOpenSettings={handleOpenSettings}
                onOpenContextMenu={handleProjectContextMenu}
              />
              {displayProjects.length === 0 ? (
                <div className="empty-state app-empty">
                  <div className="empty-state-icon">🚀</div>
                  <h3>Welcome to your dashboard</h3>
                  <p>Your AI-powered apps will appear here once you create them.</p>
                </div>
              ) : null}
            </>
          )}
        </div>

        {contextMenuState.isOpen && contextMenuState.project && (
          <AppContextMenu
            anchorX={contextMenuState.x}
            anchorY={contextMenuState.y}
            project={contextMenuState.project}
            onClose={handleCloseContextMenu}
            onOpenWorkspace={handleOpenWorkspace}
            onToggleFavorite={handleToggleFavorite}
            onLeaveApp={handleLeaveApp}
            onRemixApp={handleRemixApp}
            onDeleteApp={handleOpenDeleteModal}
            easyAppManagementEnabled={easyAppManagementEnabled}
          />
        )}
      </div>
      <RevoltModal
        isOpen={showCreateModal}
        onClose={() => {
          if (!createLoading) {
            setShowCreateModal(false);
          }
        }}
        title={createLoading ? "Creating App..." : "Create App"}
      >
        <div className="create-app-form">
          <div className="form-row">
            <label>
              Title <span style={{ color: '#ff4444' }}>*</span>
            </label>
            <RevoltInput
              value={newAppTitle}
              onChange={(e) => setNewAppTitle(e.target.value)}
              placeholder="My AI App"
              disabled={createLoading}
            />
          </div>
          <div className="form-row">
            <label>Description</label>
            <RevoltTextarea
              value={newAppDescription}
              onChange={(e) => setNewAppDescription(e.target.value)}
              placeholder="Describe what your app should do..."
              rows={4}
              disabled={createLoading}
            />
          </div>
          {appTypeOptions.length > 1 && (
            <div className="form-row">
              <label>App Type</label>
              <div className="create-app-app-type-segmented-wrap">
                <RevoltSegmentedControl
                  options={appTypeOptions}
                  value={newAppType}
                  onChange={setNewAppType}
                  size="default"
                  fitContent
                  equalWidthPadding={4}
                />
              </div>
              <p
                className="form-hint"
                style={{ color: '#8b919c', fontSize: '0.82rem' }}
              >
                {(() => {
                  const def = getSelectableAppTypes({ isDesktop: envUtils.device.isDesktopApp() })
                    .find((t) => t.id === newAppType);
                  if (!def) return '';
                  const envNote = def.runEnvironment === 'desktop'
                    ? ' Can only be opened inside Revolt Desktop.'
                    : '';
                  return def.description + envNote;
                })()}
              </p>
            </div>
          )}
          {newAppType === 'local' && (
            <div className="form-row">
              <label>
                Working Directory <span style={{ color: '#ff4444' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                <RevoltInput
                  value={newAppWorkingDirectory}
                  onChange={(e) => setNewAppWorkingDirectory(e.target.value)}
                  placeholder="No folder selected"
                  disabled={createLoading}
                  readOnly
                />
                <RevoltButton
                  variant="outline"
                  onClick={handleChooseWorkingDirectory}
                  disabled={createLoading}
                >
                  Choose Folder...
                </RevoltButton>
              </div>
              <p
                className="form-hint"
                style={{ color: '#8b919c', fontSize: '0.82rem' }}
              >
                Pick any folder on your computer. If you also select a template below, its files will be copied into this folder; otherwise the folder is left untouched.
              </p>
              {workingDirectoryError && (
                <p className="form-hint error" style={{ color: '#ff6b6b' }}>
                  {workingDirectoryError}
                </p>
              )}
            </div>
          )}
          <div className="form-row">
            <label>
              Template
              {newAppType === 'local' && (
                <span style={{ color: '#8b919c', fontWeight: 'normal', marginLeft: 6 }}>
                  (optional)
                </span>
              )}
            </label>
            <DropdownSelect
              value={newAppTemplate}
              options={
                newAppType === 'local'
                  ? [
                      { value: '', label: 'None' },
                      ...filteredTemplateOptions,
                    ]
                  : filteredTemplateOptions
              }
              onChange={(e) => setNewAppTemplate(e.target.value)}
              disabled={createLoading || (newAppType !== 'local' && !filteredTemplateOptions.length)}
            />
            <p
              className="form-hint"
              style={{ color: '#8b919c', fontSize: '0.82rem' }}
            >
              {newAppType === 'local' && !newAppTemplate
                ? 'No template selected. The working directory will be created/used as-is.'
                : (!filteredTemplateOptions.length && templatesLoadedOnce
                    ? `No templates available for this app type yet.`
                    : (selectedTemplate?.description
                        || (newAppTemplate ? 'Loading template details...' : 'Select a template to see its description.')))}
            </p>
            {templateError && (
              <p className="form-hint error">Templates unavailable: {templateError}</p>
            )}
          </div>
          <div className="create-app-actions">
            <RevoltButton
              variant="outline"
              onClick={() => setShowCreateModal(false)}
              disabled={createLoading}
            >
              {createLoading ? 'Creating...' : 'Cancel'}
            </RevoltButton>
            <RevoltButton
              variant="primary"
              onClick={handleSubmitCreate}
              disabled={
                createLoading
                || !newAppTitle
                || (newAppType === 'local'
                    ? !newAppWorkingDirectory
                    : !newAppTemplate)
              }
              title={
                createLoading
                  ? 'Creating your app...'
                  : !newAppTitle
                    ? 'Enter a title to continue'
                    : newAppType === 'local' && !newAppWorkingDirectory
                      ? 'Pick a working directory to continue'
                      : 'Create your new app'
              }
            >
              {createLoading ? 'Creating...' : 'Create'}
            </RevoltButton>
          </div>
        </div>
      </RevoltModal>

      {createdApp && (
        <RevoltModal
          isOpen={showSuccessModal}
          onClose={handleSuccessModalClose}
          title="App Created Successfully!"
        >
          <div className="app-creation-success-modal">
            <div className="app-creation-success-card">
              <div className="app-creation-success-top">
                <div className="app-creation-success-title-row">
                  <h3 className="app-creation-success-title">
                    {createdApp.name}
                    <span
                      className="app-creation-success-owner-icon owned"
                      title="You own this app"
                    >
                      <i className="fa-solid fa-circle-check"></i>
                    </span>
                  </h3>
                </div>
                <div className="app-creation-success-actions">
                  <RevoltButton
                    variant="ghost"
                    size="sm"
                    onClick={handleGoToSettings}
                    aria-label="Open settings"
                  >
                    <i className="fa-solid fa-gear"></i>
                  </RevoltButton>
                  <RevoltButton
                    variant="accent"
                    size="sm"
                    onClick={handleGoToWorkspace}
                  >
                    Workspace
                  </RevoltButton>
                </div>
              </div>
              {createdApp.description && (
                <p className="app-creation-success-description">{createdApp.description}</p>
              )}
              <div className="app-creation-success-meta">
                <span className="app-creation-success-date">
                  Created: {new Date(createdApp.createdAt).toLocaleDateString()}
                </span>
                <TemplateBadge template={createdApp.template} appType={createdApp.appType} flip />
              </div>
            </div>
          </div>
        </RevoltModal>
      )}

      <AppCreationErrorModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setErrorData(null);
        }}
        errorCode={errorData?.errorCode}
        errorMessage={errorData?.errorMessage}
        details={errorData?.details}
      />

      <RevoltModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        title="Leave App"
      >
        <div className="leave-app-confirmation">
          <p>Are you sure you want to leave <strong>{leaveModalProject?.name || 'this app'}</strong>?</p>
          <p className="leave-app-warning">
            You will no longer have access to this app and will need to be re-invited to rejoin.
          </p>
          <div className="leave-app-actions">
            <RevoltButton variant="outline" onClick={() => setShowLeaveModal(false)}>
              Cancel
            </RevoltButton>
            <RevoltButton variant="danger" onClick={handleConfirmLeaveApp}>
              Leave App
            </RevoltButton>
          </div>
        </div>
      </RevoltModal>

      <RemixModal
        isOpen={showRemixModal}
        onClose={() => setShowRemixModal(false)}
        onConfirmRemix={handleConfirmRemix}
        project={remixModalProject}
        isLoading={remixModalLoading}
      />

      {showDeleteModal && deleteModalProject && (
        <RevoltModal
          isOpen={showDeleteModal}
          onClose={() => {
            if (!deleteLoading) {
              setShowDeleteModal(false);
              setDeleteInput('');
              setIsEasyDelete(false);
              setDeleteLoading(false);
            }
          }}
          title="Delete App"
        >
          <div className="delete-modal-body">
            {deleteLoading ? (
              <p className="delete-warning">
                Deleting app "{deleteModalProject?.name}"... Please wait.
              </p>
            ) : (
              <p className="delete-warning">
                This will permanently delete the app and its data. This action cannot be undone.
              </p>
            )}
            <div className="delete-summary">
              <div><strong>Name:</strong> {deleteModalProject?.name}</div>
              <div><strong>Created:</strong> {deleteModalProject?.createdAtFormatted || deleteModalProject?.createdAt}</div>
              <div><strong>Template:</strong> {deleteModalProject?.template} ({deleteModalProject?.appType})</div>
            </div>
            {!isEasyDelete && (
              <>
                <p className="delete-instructions">
                  Type "{deleteModalProject?.name}" to confirm your action.
                </p>
                <div className="delete-input-row">
                  <RevoltInput
                    value={deleteInput}
                    onChange={(e) => setDeleteInput(e.target.value)}
                    placeholder="Type the app name to confirm"
                    disabled={deleteLoading}
                  />
                  <RevoltButton
                    variant="ghost"
                    disabled={deleteLoading}
                    onClick={() => navigator?.clipboard?.writeText(deleteModalProject?.name || '')}
                  >
                    Copy Name
                  </RevoltButton>
                </div>
              </>
            )}
            <div className="delete-actions">
              <RevoltButton
                variant="ghost"
                disabled={deleteLoading}
                onClick={() => {
                  if (!deleteLoading) {
                    setShowDeleteModal(false);
                    setDeleteInput('');
                    setIsEasyDelete(false);
                    setDeleteLoading(false);
                  }
                }}
              >
                {deleteLoading ? 'Deleting...' : 'Cancel'}
              </RevoltButton>
              <RevoltButton
                variant="danger"
                disabled={deleteLoading || (isEasyDelete ? false : (deleteInput || '').trim() !== (deleteModalProject?.name || ''))}
                onClick={async () => {
                  if (!deleteLoading) {
                    await handleDeleteApp();
                    setDeleteInput('');
                    setIsEasyDelete(false);
                    setShowDeleteModal(false);
                  }
                }}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </RevoltButton>
            </div>
          </div>
        </RevoltModal>
      )}
    </MainLayout>
  );
}
